import { Color } from "@repo/app-types/constants/colors";
import { CustomFieldType } from "@repo/app-types/entities/customField";
import { type FormField, FormFieldType } from "@repo/app-types/entities/form";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { DragHandle } from "@repo/ui/DraggableList/dragHandle";
import { Input } from "@repo/ui/Inputs/input";
import { Pill } from "@repo/ui/Pills/pill";
import { Size } from "@repo/ui/helpers/helpers";
import { IconTrash } from "@tabler/icons-react";
import { type FC, type ReactNode, useState } from "react";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";
import { UserCustomFieldSelect } from "../../userCustomFieldSelect.js";

export type FormFieldWithValidation = FormField & {
  errorMessage: string | undefined;
};

export interface BaseFormQuestionCardProps {
  index: number;
  type: FormFieldType;
  children: ReactNode;
  onDelete: () => void;
}

export const BaseFormQuestionCard: FC<BaseFormQuestionCardProps> = (props) => {
  const { onDelete } = props;

  return (
    <div className="my-2 w-full rounded-lg bg-white shadow-sm">
      <div className="group flex items-center justify-between border-b border-border-light px-3 py-2">
        <DragHandle>
          <div className="flex items-center space-x-2">
            <Pill label={props.index} color={Color.Gray} size={Size.SMALL} />
            <div className="text-sm font-medium">
              {getQuestionTypeLabel(props.type)}
            </div>
          </div>
        </DragHandle>

        <Can I={PermissionActions.UPDATE} a={PermissionSubjects.FORMS}>
          <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100">
            <IconButton
              variant={ButtonVariant.Secondary}
              icon={<IconTrash className="size-4 text-red-500" />}
              onClick={onDelete}
            />
          </div>
        </Can>
      </div>

      <div className="p-3">{props.children}</div>
    </div>
  );
};

export function getQuestionTypeLabel(type: FormFieldType) {
  switch (type) {
    case FormFieldType.Dropdown:
      return "Single choice dropdown";
    case FormFieldType.TextInput:
      return "Text input";
    default:
      return type;
  }
}

const minTitleLength = 5;
const maxTitleLength = 200;

function mayGetTitleValueErrorMessage(value: string) {
  if (value.trim().length < minTitleLength) {
    return `Question must be at least ${minTitleLength} characters`;
  } else if (value.length > maxTitleLength) {
    return `Question must be less than ${maxTitleLength} characters`;
  }

  return undefined;
}

const supportedTargetCustomFieldTypes = [CustomFieldType.Text] as const;

export interface FieldFormQuestionCardProps {
  index: number;
  field: FormFieldWithValidation;
  onChange: (updatedField: FormFieldWithValidation) => void;
  onDelete: () => void;
  children?: ReactNode;
}

export const FieldFormQuestionCard: FC<FieldFormQuestionCardProps> = (
  props,
) => {
  const { field, onDelete, onChange, children } = props;

  const { errorMessage } = field;

  const [titleValue, setTitleValue] = useState(field.label);

  const onTitleBlur = () => {
    const newErrorMessage = mayGetTitleValueErrorMessage(titleValue);

    onChange({
      ...field,
      label: titleValue,
      errorMessage: newErrorMessage,
    });
  };

  const onTargetCustomFieldChange = (
    customFieldDefinitionId: string | null,
  ) => {
    onChange({
      ...field,
      target_custom_field_definition_id: customFieldDefinitionId,
    });
  };

  return (
    <BaseFormQuestionCard
      index={props.index + 1}
      type={field.type}
      onDelete={onDelete}
    >
      <div className="items-center space-y-2">
        <div className="flex items-center space-x-2">
          <div className="w-full">
            <Input
              id={`${field.id}-title`}
              value={titleValue}
              onChange={(evt) => {
                setTitleValue(evt.target.value);

                if (errorMessage) {
                  onChange({
                    ...field,
                    errorMessage: undefined,
                  });
                }
              }}
              onBlur={onTitleBlur}
              minLength={minTitleLength}
              maxLength={maxTitleLength}
              errorDescription={errorMessage}
              placeholder={getTitleValuePlaceholder(field.type)}
            />
          </div>
        </div>

        {children}

        <div className="flex flex-col items-start">
          <UserCustomFieldSelect
            label="Save a copy to a custom field"
            placeholder="Select a custom field (optional)"
            noneLabel="None"
            selectedCustomFieldDefinitionId={
              field.target_custom_field_definition_id ?? null
            }
            onChange={onTargetCustomFieldChange}
            fieldTypes={supportedTargetCustomFieldTypes}
          />
        </div>
      </div>
    </BaseFormQuestionCard>
  );
};

function getTitleValuePlaceholder(type: FormFieldType) {
  switch (type) {
    case FormFieldType.TextInput:
      return "What is your LinkedIn URL?";
    default:
      return "Enter a question title";
  }
}
